import React, { useCallback, useState } from 'react';
import { Button, Form, Input as FormInput, Select, DatePicker, Space, Typography, Upload, ConfigProvider, Row, Col, Card, message, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { UploadOutlined } from '@ant-design/icons';
import { themeConfig } from '../theme/colors';
import { addPatient } from '../api/addpatientApi';

const { Title } = Typography;
const { Option } = Select;

const AddPatientPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      console.log('Form Values:', values);
      
      // Call the API to add patient to NocoDB
      const result = await addPatient(values);
      
      message.success('Patient added successfully!');
      console.log('Patient created with ID:', result.recId || result.id);
      
      form.resetFields();
      navigate('/patient');
    } catch (error) {
      if (error.errorFields) {
        console.log('Validation Failed:', error);
        message.error('Please fill in all required fields');
      } else {
        console.error('Error adding patient:', error);
        message.error('Failed to add patient. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [form, navigate]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    navigate('/patient');
  }, [form, navigate]);

  // Add effect to handle keyboard events
  React.useEffect(() => {
    const handleKeyPress = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        if (!loading) {
          handleSubmit();
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleSubmit, handleCancel, loading]);

  // Custom upload validation
  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG files!');
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB!');
    }
    return isJpgOrPng && isLt2M;
  };

  return (
    <ConfigProvider theme={themeConfig}>
      <Spin spinning={loading} tip="Saving patient data...">
        <div 
          style={{ 
            padding: '24px', 
            background: themeConfig.token.colorBgLayout,
            minHeight: 'calc(100vh - 120px)',
          }}
        >
          <Title level={3} style={{ color: themeConfig.token.colorTextHeading, marginBottom: 24 }}>
            Add Patient (Ctrl/⌘ + S to save, Esc to cancel)
          </Title>
          <Form 
            form={form} 
            layout="vertical"
            onKeyPress={(e) => {
              if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                e.preventDefault();
              }
            }}
          >
            {/* Name Section */}
            <Card title="Name" style={{ marginBottom: 24 }} bordered={false}>
              <Row gutter={[16, 8]} align="middle">
                <Col xs={24} sm={8}>
                  <Form.Item
                    name={['name', 'firstName']}
                    label="First Name"
                    rules={[{ required: true, message: 'Please enter first name' }]}
                  >
                    <FormInput placeholder="First Name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name={['name', 'middleName']}
                    label="Middle Name"
                  >
                    <FormInput placeholder="Middle Name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name={['name', 'lastName']}
                    label="Last Name"
                    rules={[{ required: true, message: 'Please enter last name' }]}
                  >
                    <FormInput placeholder="Last Name" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Contact Section */}
            <Card title="Contact Information" style={{ marginBottom: 24 }} bordered={false}>
              <Row gutter={[16, 8]} align="middle">
                <Col xs={24} sm={8}>
                  <Form.Item
                    name={['contact', 'mobile']}
                    label="Mobile Number"
                    rules={[
                      { required: true, message: 'Please enter mobile number' },
                      { pattern: /^[0-9]{10}$/, message: 'Please enter a valid 10-digit mobile number' }
                    ]}
                  >
                    <FormInput placeholder="Mobile Number" maxLength={10} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name={['contact', 'email']}
                    label="Email"
                    rules={[
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <FormInput placeholder="Email" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name={['contact', 'alternatePhone']}
                    label="Alternate Phone"
                    rules={[
                      { pattern: /^[0-9]*$/, message: 'Please enter numbers only' }
                    ]}
                  >
                    <FormInput placeholder="Alternate Phone" maxLength={10} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Gender Section */}
            <Card title="Gender" style={{ marginBottom: 24 }} bordered={false}>
              <Form.Item name="gender" rules={[{ required: true, message: 'Please select gender' }]}>
                <Select placeholder="Select Gender">
                  <Option value="female">Female</Option>
                  <Option value="male">Male</Option>
                  <Option value="other">Other</Option>
                  <Option value="unknown">Unknown</Option>
                </Select>
              </Form.Item>
            </Card>

            {/* Birth Date Section */}
            <Card title="Birth Date" style={{ marginBottom: 24 }} bordered={false}>
              <Form.Item name="birthDate" rules={[{ required: true, message: 'Please select birth date' }]}>
                <DatePicker 
                  format="DD/MM/YYYY" 
                  style={{ width: '100%' }} 
                  disabledDate={(current) => current && current.isAfter(new Date())}
                  placeholder="Select birth date"
                />
              </Form.Item>
            </Card>

            {/* Address Section */}
            <Card title="Address" style={{ marginBottom: 24 }} bordered={false}>
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={4}>
                  <Form.Item
                    name={['address', 'use']}
                    label="Use"
                    rules={[{ required: true, message: 'Please select use' }]}
                  >
                    <Select placeholder="Use">
                      <Option value="home">Home</Option>
                      <Option value="work">Work</Option>
                      <Option value="temp">Temp</Option>
                      <Option value="old">Old</Option>
                      <Option value="billing">Billing</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={4}>
                  <Form.Item
                    name={['address', 'type']}
                    label="Type"
                    rules={[{ required: true, message: 'Please select type' }]}
                  >
                    <Select placeholder="Type">
                      <Option value="postal">Postal</Option>
                      <Option value="physical">Physical</Option>
                      <Option value="both">Both</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={4}>
                  <Form.Item name={['address', 'line1']} label="Line 1">
                    <FormInput placeholder="Line 1" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={4}>
                  <Form.Item name={['address', 'line2']} label="Line 2">
                    <FormInput placeholder="Line 2" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={4}>
                  <Form.Item name={['address', 'city']} label="City">
                    <FormInput placeholder="City" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={4}>
                  <Form.Item name={['address', 'state']} label="State">
                    <FormInput placeholder="State" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={4}>
                  <Form.Item name={['address', 'postalCode']} label="Postal Code">
                    <FormInput placeholder="Postal Code" maxLength={6} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Marital Status Section */}
            <Card title="Marital Status" style={{ marginBottom: 24 }} bordered={false}>
              <Form.Item name="maritalStatus">
                <Select placeholder="Select Marital Status">
                  <Option value="annulled">Annulled</Option>
                  <Option value="divorced">Divorced</Option>
                  <Option value="legally separated">Legally Separated</Option>
                  <Option value="married">Married</Option>
                  <Option value="polygamous">Polygamous</Option>
                  <Option value="never married">Never Married</Option>
                  <Option value="domestic partner">Domestic Partner</Option>
                  <Option value="unmarried">Unmarried</Option>
                  <Option value="widowed">Widowed</Option>
                </Select>
              </Form.Item>
            </Card>

            {/* Photo Section */}
            <Card title="Photo" style={{ marginBottom: 24 }} bordered={false}>
              <Form.Item
                name="photo"
                valuePropName="fileList"
                getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
              >
                <Upload
                  name="photo"
                  listType="picture"
                  beforeUpload={beforeUpload}
                  maxCount={1}
                  accept="image/*"
                >
                  <Button icon={<UploadOutlined />} style={{ borderRadius: themeConfig.token.borderRadius }}>
                    Upload Photo (Max 2MB)
                  </Button>
                </Upload>
              </Form.Item>
            </Card>

            {/* General Practitioner Section */}
            <Card title="General Practitioner" style={{ marginBottom: 24 }} bordered={false}>
              <Form.Item name={['generalPractitioner', 'organization']} label="Organization">
                <FormInput placeholder="Organization" />
              </Form.Item>
            </Card>

            {/* Managing Organization Section */}
            <Card title="Managing Organization" style={{ marginBottom: 24 }} bordered={false}>
              <Form.Item name="managingOrganization" label="Managing Organization">
                <FormInput placeholder="Managing Organization" />
              </Form.Item>
            </Card>

            {/* Form Actions */}
            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  onClick={handleSubmit}
                  loading={loading}
                  style={{ borderRadius: themeConfig.token.borderRadius }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !loading) {
                      handleSubmit();
                    }
                  }}
                >
                  {loading ? 'Creating...' : 'Create (Ctrl/⌘ + S)'}
                </Button>
                <Button 
                  onClick={handleCancel}
                  disabled={loading}
                  style={{ borderRadius: themeConfig.token.borderRadius }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCancel();
                    }
                  }}
                >
                  Cancel (Esc)
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </Spin>
    </ConfigProvider>
  );
};

export default AddPatientPage;